import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal, mergeAndStringifyEntityObjects } from '@domain-group/javascript-utils'

import { loginAsEntityUser } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as brandActions from '@redux/modules/brand'

import defaultFormState, { requiredFields } from '@models/brand'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { ModuleState } from '@redux/modules/types'
import type {
  AttachmentModel, BrandModel, BrandRequestOptions,
} from '@models/types'

export const generateUrls = (brand?: Partial<BrandModel>) => {
  const { id } = brand || {}

  return {
    brandsIndexUrl: '#/systemManager/brands?hidden=false',
    editBrandCustomFieldsUrl: `#/systemManager/brands/${id}/edit/customFields`,
    editBrandImageSettingsUrl: `#/systemManager/brands/${id}/edit/imageSettings`,
    editBrandRelationsUrl: `#/systemManager/brands/${id}/edit/relations`,
    editBrandSettingsUrl: `#/systemManager/brands/${id}/edit/settings`,
    editBrandUrl: `#/systemManager/brands/${id}/edit`,
    hiddenBrandsIndexUrl: '#/systemManager/brands?hidden=true',
    viewBrandAgenciesOverviewUrl: `#/systemManager/brands/${id}/edit/agenciesOverview`,
  }
}

type CreateBrandParams = {
  brandParams: Partial<BrandModel>,
  dispatch: AppDispatch,
  requestOptions?: BrandRequestOptions,
}

const createBrand = (params: CreateBrandParams) => {
  const { dispatch, brandParams } = params
  const { createBrand: createFn } = brandActions

  const { options } = brandParams

  const updatedParams = {
    ...brandParams,
    options: JSON.stringify(options),
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editBrandUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editBrandUrl,
      }
    }

    return response
  })
}

type ExportBrandParams = {
  brand: BrandModel,
  dispatch: AppDispatch,
}

export const exportBrand = (params: ExportBrandParams) => {
  const { brand, dispatch } = params
  const { exportBrand: exportFn } = brandActions

  return dispatch(exportFn(brand))
}

type UpdateBrandParams = {
  brand: BrandModel,
  brandParams: Partial<BrandModel>,
  dispatch: AppDispatch,
  requestOptions?: BrandRequestOptions,
}

export const updateBrand = (params: UpdateBrandParams) => {
  const {
    dispatch, brand, brandParams, requestOptions,
  } = params
  const { updateBrand: updateFn } = brandActions

  const stringifyObjectKeys = ['data', 'options']
  const updatedParams = mergeAndStringifyEntityObjects(brand, brandParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type LoginAsBrandParams = {
  brand: BrandModel,
  dispatch: AppDispatch,
}

const loginAsBrand = (params: LoginAsBrandParams) => {
  const { brand, dispatch } = params

  return dispatch(loginAsEntityUser('brands', brand.id, {})).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      return {
        success,
        // @ts-expect-error TS2339
        redirectUrl: data.url,
      }
    }

    return response
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useBrandForm(
  brand: Partial<BrandModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const brandForm = useForm(
    defaultFormState,
    { entity: brand, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [brand.id, brand.cache_key],
  )

  return {
    ...brandForm,
  }
}

export type UseBrandFormPayload = ReturnType<typeof useBrandForm>

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: BrandRequestOptions,
}

function useBrand(initEntity: Partial<BrandModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: brand }: { entity: BrandModel } = useLatestEntity(initEntity, 'brands')
  const { id } = brand

  const dispatch = useDispatch()

  const {
    require_template_specialists: requireTemplateSpecialists = false,
    template_developer_user_ids: templateDeveloperUserIds = [],
  } = digObject(brand, 'options', {})

  const hasTemplateDevelopers = !!templateDeveloperUserIds.length

  const { callbacks } = useContext(PageContext)

  useReduxAction(
    'brands',
    'loadBrand',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, loading, updating,
  } = useSelector(reduxState => reduxState.brands)

  const quickLinks = digObject(brand, 'options.quick_links', [])
  const hasQuickLinks = !!quickLinks.length

  const publicLinkTextAccentColour = () => {
    const brandAccentColor = digObject(brand, 'options.branded_public_links.text_accent_colour', '')
    if (brandAccentColor.length) return `#${brandAccentColor}`
    return '#02b7e2'
  }

  const publicLinkUseLightText = digObject(brand, 'options.branded_public_links.use_light_text', true)
  const publicLinkFontColor = publicLinkUseLightText ? '#ffffff' : '#404041'

  return {
    brand,
    callbacks: {
      createBrand: (brandParams: Partial<BrandModel>, entityOptions?: BrandRequestOptions) => (
        createBrand({ brandParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCustomLinks: (customPayload: {}) => launchModal({
        callbacks,
        modalKey: 'ManageEntityQuickLinksModal',
        payload: { ...customPayload, entity: brand, entityKey: 'brands' },
      }),
      editBrandSSO: () => launchModal({
        callbacks,
        modalKey: 'ManageBrandSSOModal',
        payload: { brand },
      }),
      editBrandSettings: () => launchModal({
        callbacks,
        modalKey: 'BrandSettingsModal',
        payload: { brand },
      }),
      exportBrand: () => exportBrand({ brand, dispatch }),
      loginAsBrand: () => loginAsBrand({ brand, dispatch }),
      manageBrandImageSettingsPermissions: () => launchModal({
        callbacks,
        modalKey: 'ManageBrandImageSettingsPermissionsModal',
        payload: { brand },
      }),
      manageBrandPublicLinkSettingsModal: () => launchModal({
        callbacks,
        modalKey: 'ManageBrandPublicLinkSettingsModal',
        payload: { brand },
      }),
      manageBrandPublicLinkAttachments: (attachment: Partial<AttachmentModel>) => launchModal({
        callbacks,
        modalKey: 'ManageBrandPublicLinkAttachmentsModal',
        payload: { attachment, brand },
      }),
      manageBrandTemplateDevelopers: () => launchModal({
        callbacks,
        modalKey: 'ManageBrandTemplateDevelopersModal',
        payload: { brand },
      }),
      updateBrand: (brandParams: Partial<BrandModel>, entityOptions?: BrandRequestOptions) => (
        updateBrand({
          brand, brandParams, dispatch, requestOptions: entityOptions,
        })
      ),
      uploadBrandImage: (customPayload: object) => launchModal({
        callbacks,
        modalKey: 'UploadBrandImageModal',
        payload: { brand, ...customPayload },
      }),
    },
    creating,
    hasQuickLinks,
    hasTemplateDevelopers,
    loading,
    publicLinkFontColor,
    publicLinkTextAccentColour: publicLinkTextAccentColour(),
    publicLinkUseLightText,
    quickLinks,
    requireTemplateSpecialists,
    templateDeveloperUserIds,
    updating,
    urls: generateUrls(brand),
  }
}

export type UseBrandPayload = ReturnType<typeof useBrand>

export default useBrand
